import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Gift, Check, Users, LogIn } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';

interface ReferralSectionProps {
  referralCode: string | undefined;
  userId: string | undefined;
}

/** Matches server/client bootstrap: REF + first 5 chars of uid (uppercase). */
function deriveReferralCodeFromUid(uid: string): string {
  return ('REF' + uid.slice(0, 5)).toUpperCase();
}

export function ReferralSection({
  referralCode,
  userId,
}: ReferralSectionProps) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  /* Guest (lead-only): show clear CTA — was hidden before because userId was missing */
  if (!userId) {
    return (
      <div className="mx-4 mb-4 rounded-2xl border-2 border-primary/35 bg-gradient-to-br from-primary/12 via-primary/5 to-transparent p-4 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0 shadow-md">
            <Users className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-foreground">شبكة الإحالة</h3>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              سجّل الدخول لمشاركة كودك ومتابعة شبكة الإحالة وربح نسبة من كاش باك المشتريات
              على أكثر من مستوى.
            </p>
            <Button
              type="button"
              className="mt-3 rounded-xl h-10"
              onClick={() => navigate('/login')}
            >
              <LogIn className="w-4 h-4 ml-2" />
              تسجيل الدخول
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const displayCode =
    referralCode && referralCode.trim().length > 0
      ? referralCode.trim().toUpperCase()
      : deriveReferralCodeFromUid(userId);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayCode);
      setCopied(true);
      toast({ title: 'تم النسخ', description: 'تم نسخ كود الإحالة' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'خطأ', description: 'تعذّر نسخ الكود', variant: 'destructive' });
    }
  };

  return (
    <section
      className="mx-4 mb-4 rounded-2xl border-2 border-primary/30 bg-card overflow-hidden shadow-sm"
      aria-label="إحالة الأصدقاء"
    >
      <div className="bg-primary/10 px-4 py-2.5 border-b border-primary/20 flex items-center gap-2">
        <Users className="w-4 h-4 text-primary shrink-0" />
        <h2 className="text-sm font-bold text-foreground">إحالة الأصدقاء</h2>
      </div>

      <div className="p-4 space-y-4">
        {/* 1 — Your code */}
        <div>
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
          <Gift className="w-5 h-5 text-primary" />
        </div>
        <div>
          <p className="font-semibold text-foreground text-sm">شارك كودك واربح</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            انسخ الكود وأرسله لأصدقائك عند التسجيل
          </p>
        </div>
      </div>

          <div className="flex items-stretch gap-2">
            <div className="flex-1 flex items-center justify-center bg-secondary rounded-xl py-3 px-4 ring-1 ring-primary/10">
              <span className="text-lg font-extrabold tracking-[0.2em] text-primary font-mono select-all">
                {displayCode}
        </span>
      </div>
        <button
              type="button"
          onClick={handleCopy}
              className="shrink-0 flex items-center justify-center gap-2 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-semibold active:opacity-90 transition-opacity min-w-[100px] shadow-sm"
        >
          {copied ? (
            <>
                  <Check className="w-4 h-4" />
                  تم
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
                  نسخ
            </>
          )}
        </button>
          </div>
        </div>
      </div>
    </section>
  );
}
